import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';
import { BaseObject } from '@adgate/common/types/base-object.type';
import { forkJoin, Observable } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import moment from 'moment';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { CommercialType } from '@adgate/common/types/commercial-type.type';
import { ProductionService } from '@adgate/modules/production/services/production-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { LinkMasterCommercialDialogComponent } from '../link-master-commercial-dialog-component/link-master-commercial-dialog-component';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';

@Component({
  selector: 'app-production-request-edit-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatChipsModule,
    ObjectDescPipe
  ],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: AdGateConstants.ADGATE_DATE_FORMATS }
  ],
  templateUrl: './production-request-edit-dialog-component.html',
  styleUrl: './production-request-edit-dialog-component.scss',
})
export class ProductionRequestEditDialogComponent implements OnInit
{
  private dialogRef: MatDialogRef<ProductionRequestEditDialogComponent> = inject(MatDialogRef<ProductionRequestEditDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogService: DialogService = inject(DialogService);
  private commonObjectService: CommonObjectService = inject(CommonObjectService);
  private productionService: ProductionService = inject(ProductionService);
  public data: ProductionRequest = inject(MAT_DIALOG_DATA, { optional: false });

  public comparatorService: ComparatorService = inject(ComparatorService);

  @ViewChild('showInput') showInput!: ElementRef<HTMLInputElement>;

  requestForm: FormGroup;
  versionTypes: BaseObject[] = [];
  shows: string[] = [];
  clients: string[] = [];
  filteredShows!: Observable<string[]>;
  filteredClients!: Observable<string[]>;
  selectedShows: string[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor()
  {
    this.requestForm = this.fb.group({
      name: ['', [Validators.required, CustomValidators.invalidCharactersValidator()]],
      client: ['', [Validators.required, CustomValidators.invalidCharactersValidator()]],
      requireVoiceover: [false],
      requireSample: [false],
      airDate: ['',[Validators.required]],
      voiceoverDetails: [{value: '', disabled: true}, [Validators.required, CustomValidators.invalidCharactersValidator()]],
      origin: ['', [Validators.required, CustomValidators.invalidCharactersValidator()]],
      showInput: [''],
      defaultVersionType: [{value: '', disabled: this.data.id != -1}, Validators.required],
      notes: [''],
      masterCommercial: [{value: '', disabled: true}]
    });
  }

  ngOnInit(): void
  {
    this.dialogService.showProgress();
    forkJoin([
      this.commonObjectService.getCommercialTypes(CommercialType.USAGE_PRODUCTIONVERSION),
      this.productionService.getProductionRequestShows(),
      this.productionService.getProductionRequestClients()
    ]).subscribe({
      next: (results:RESTApplicationResponse[]) => {
        this.versionTypes = results[0].data;
        this.shows = results[1].data;
        this.clients = results[2].data;

        // Set up autocomplete observables after data is loaded
        this.filteredShows = this.requestForm.get('showInput')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterShows(value || ''))
        );

        this.filteredClients = this.requestForm.get('client')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterClients(value || ''))
        );

        if (this.data.id !== -1) {
          this.requestForm.get('name')?.setValue(this.data.name);
          this.requestForm.get('client')?.setValue(this.data.client);
          this.requestForm.get('requireVoiceover')?.setValue(this.data.requireVoiceover);
          this.requestForm.get('requireSample')?.setValue(this.data.requireSample);
          this.requestForm.get('airDate')?.setValue(this.data.airDate ? moment(this.data.airDate) : null);
          this.requestForm.get('voiceoverDetails')?.setValue(this.data.voiceoverDetails);
          this.requestForm.get('origin')?.setValue(this.data.origin);
          this.requestForm.get('defaultVersionType')?.setValue(this.data.defaultVersionType);
          this.requestForm.get('notes')?.setValue(this.data.notes);
          this.requestForm.get('masterCommercial')?.setValue(this.data.masterCommercialRefCode);
          this.selectedShows = [...this.data.shows];

          // Enable voiceover details if require voiceover is true when editing
          if (this.data.requireVoiceover) {
            this.requestForm.get('voiceoverDetails')?.enable();
          }
        }

        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog( err.message );
      }
    });
  }

  onRequireVoiceoverChange(requireVoiceover: boolean): void
  {
    const voiceoverDetailsControl = this.requestForm.get('voiceoverDetails');
    if (requireVoiceover) {
      voiceoverDetailsControl?.enable();
    } else {
      voiceoverDetailsControl?.disable();
      voiceoverDetailsControl?.setValue('');
    }
  }

  onLinkMasterCommercial()
  {
    const dialogRef = this.dialogService.openAnyDialog(LinkMasterCommercialDialogComponent, {
      width: '1100px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Commercial | null) => result != null),
    ).subscribe({
      next: (r:Commercial) => {
        this.data.masterCommercialId = r.id;
        this.data.masterCommercialRefCode = r.referenceCode;
        this.requestForm.get('masterCommercial')?.setValue(r.referenceCode);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }
  private _filterShows(value: string): string[]
  {
    const filterValue = value.toLowerCase();
    return this.shows.filter(show =>
      show.toLowerCase().includes(filterValue) && !this.selectedShows.includes(show)
    );
  }

  private _filterClients(value: string): string[]
  {
    const filterValue = value.toLowerCase();
    return this.clients.filter(client =>
      client.toLowerCase().includes(filterValue)
    );
  }

  addShow(event: MatAutocompleteSelectedEvent): void
  {
    const show = event.option.value;

    if (show && !this.selectedShows.some(item => item.toLowerCase() === show.toLowerCase())) {
      this.selectedShows.push(show);
    }

    if (this.showInput) {
      this.showInput.nativeElement.value = '';
    }

    this.requestForm.get('showInput')?.setValue('');
  }

  addShowFromInput(event: MatChipInputEvent): void
  {
    const value = (event.value || '').trim();

    if (value && !this.selectedShows.some(item => item.toLowerCase() === value.toLowerCase())) {
      this.selectedShows.push(value.toUpperCase());
      this.requestForm.get('showInput')?.setValue('');
    }

    event.chipInput!.clear();
  }

  removeShow(show: string): void
  {
    const index = this.selectedShows.indexOf(show);
    if (index >= 0) {
      this.selectedShows.splice(index, 1);
    }
  }

  onCancel(): void
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void
  {
    if (this.requestForm.valid && this.selectedShows.length > 0) {
      this.data.name = this.requestForm.get('name')?.value;
      this.data.client = this.requestForm.get('client')?.value;
      this.data.requireVoiceover = this.requestForm.get('requireVoiceover')?.value;
      this.data.requireSample = this.requestForm.get('requireSample')?.value;
      this.data.voiceoverDetails = this.requestForm.get('voiceoverDetails')?.value;
      this.data.origin = this.requestForm.get('origin')?.value;
      this.data.defaultVersionType = this.requestForm.get('defaultVersionType')?.value;
      this.data.notes = this.requestForm.get('notes')?.value;
      this.data.shows = [...this.selectedShows];

      const airDate = this.requestForm.get('airDate')?.value;
      this.data.airDate = (airDate.unix() * 1000);

      this.dialogRef.close(this.data);
    } else if (this.selectedShows.length === 0) {
      this.dialogService.openErrorDialog('At least one show is required');
    }
  }
}
